import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormControl} from '@angular/forms';
import {ApplicationCounty, PledgeType} from '../../../../services/api.client';
import {combineLatest} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Disposable} from '../../../../utilities/Disposable';
import {ApplicationCountyService} from '../../../../services/applicationCounty.service';
import {ActivatedRoute, Router} from '@angular/router';
import {PledgeTypeService} from '../../../../services/PledgeTypeService';
import {Validators} from '../../../../utilities/Validators';

@Component({
    selector: 'app-manage-application-county',
    templateUrl: './manage-application-county.component.html',
    styleUrls: ['./manage-application-county.component.scss'],
    standalone: false
})
export class ManageApplicationCountyComponent extends Disposable implements OnInit {

    form = this._formBuilder.group({
        name: new FormControl({value: '', disabled: true}),
        isStlWaterActiveInternally: new FormControl(false),
        isStlWaterActiveExternally: new FormControl(false),
        selectedStlWaterFundAdministrativePledgeType: new FormControl('', Validators.requiredWhen('isStlWaterActiveExternally', true)),
        isUhonsActiveInternally: new FormControl(false),
        isUhonsActiveExternally: new FormControl(false),
        selectedUhonsFundAdministrativePledgeType: new FormControl('', Validators.requiredWhen('isUhonsActiveExternally', true))
    });

    get countyNameControl(): FormControl {
        return this.form.get('name') as FormControl;
    }

    get isStlWaterActiveInternalControl(): FormControl {
        return this.form.get('isStlWaterActiveInternally') as FormControl;
    }

    get isUhonsActiveInternalControl(): FormControl {
        return this.form.get('isUhonsActiveInternally') as FormControl;
    }

    get isStlWaterActiveExternalControl(): FormControl {
        return this.form.get('isStlWaterActiveExternally') as FormControl;
    }

    get isUhonsActiveExternalControl(): FormControl {
        return this.form.get('isUhonsActiveExternally') as FormControl;
    }

    get selectedStlWaterFundAdministrativePledgeTypeControl(): FormControl {
        return this.form.get('selectedStlWaterFundAdministrativePledgeType') as FormControl;
    }

    get selectedUhonsFundAdministrativePledgeTypeControl(): FormControl {
        return this.form.get('selectedUhonsFundAdministrativePledgeType') as FormControl;
    }

    get isUhonsActiveExternalValue(): boolean {
        return this.isUhonsActiveExternalControl && this.isUhonsActiveExternalControl.value || false;
    }

    get isStlWaterActiveExternalValue(): boolean {
        return this.isStlWaterActiveExternalControl && this.isStlWaterActiveExternalControl.value || false;
    }

    constructor(
        private _formBuilder: FormBuilder,
        private _applicationCountyService: ApplicationCountyService,
        private _pledgeTypeService: PledgeTypeService,
        private _router: Router,
        private _route: ActivatedRoute,
    ) {
        super();
    }

    // 003-retire-ngrx (US2): page state is component-local now. The county is
    // always fetched by the :id route param (this was already the deep-link
    // path; the list's pre-seeded store handoff only skipped one GET).
    currentSelectedApplicationCounty: ApplicationCounty;
    fundAdministrativePledgeTypes: PledgeType[];

    ngOnInit(): void {
        combineLatest([
            this._route.params,
            this._pledgeTypeService.retrieveFundAdministrativePledgeTypes()
        ]).pipe(
            takeUntil(this.$destroyed),
        ).subscribe(([params, fundAdministrativePledgeTypes]) => {
            this.fundAdministrativePledgeTypes = fundAdministrativePledgeTypes.filter(pt => pt.isActive);
            if (params.id) {
                this.getApplicationCountyById(params.id);
            } else {
                this._router.navigate(['app/counties']);
            }
        });

        this.handleIsActiveExternalOnChange();
    }

    getApplicationCountyById(id: string): void {
        this._applicationCountyService.getApplicationCountyById(id)
            .pipe(takeUntil(this.$destroyed))
            .subscribe({
                next: (currentSelectedApplicationCounty) => {
                    this.currentSelectedApplicationCounty = currentSelectedApplicationCounty;

                    if (!currentSelectedApplicationCounty) {
                        this._router.navigate(['app/counties']);
                        return;
                    }

                    this.form.patchValue({
                        name: currentSelectedApplicationCounty?.name,
                        isUhonsActiveInternally: currentSelectedApplicationCounty?.isUhonsActiveInternal || false,
                        isUhonsActiveExternally: currentSelectedApplicationCounty?.isUhonsActiveExternal || false,
                        selectedUhonsFundAdministrativePledgeType: currentSelectedApplicationCounty?.uhonsFundAdministrativePledgeTypeId || '',
                        isStlWaterActiveInternally: currentSelectedApplicationCounty?.isStlWaterActiveInternal || false,
                        isStlWaterActiveExternally: currentSelectedApplicationCounty?.isStlWaterActiveExternal || false,
                        selectedStlWaterFundAdministrativePledgeType: currentSelectedApplicationCounty?.stlWaterFundAdministrativePledgeTypeId || ''
                    });

                    if (currentSelectedApplicationCounty?.isUhonsActiveExternal &&
                        !currentSelectedApplicationCounty?.uhonsFundAdministrativePledgeTypeId) {
                        this.selectedUhonsFundAdministrativePledgeTypeControl?.markAsTouched();
                    }
                    if (currentSelectedApplicationCounty?.isStlWaterActiveExternal &&
                        !currentSelectedApplicationCounty?.stlWaterFundAdministrativePledgeTypeId) {
                        this.selectedStlWaterFundAdministrativePledgeTypeControl?.markAsTouched();
                    }
                },
                error: () => {
                    this._router.navigate(['app/counties']);
                }
            });
    }

    onSave(): void {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        let uhonsFundAdministrativePledgeTypeId = this.selectedUhonsFundAdministrativePledgeTypeControl?.value || null;
        let stlWaterFundAdministrativePledgeTypeId = this.selectedStlWaterFundAdministrativePledgeTypeControl?.value || null;

        if (!this.isUhonsActiveExternalValue) {
            uhonsFundAdministrativePledgeTypeId = null;
        }
        if (!this.isStlWaterActiveExternalValue) {
            stlWaterFundAdministrativePledgeTypeId = null;
        }

        const applicationCounty = ApplicationCounty.fromJS({
            id: this.currentSelectedApplicationCounty.id,
            name: this.countyNameControl?.value,
            isUhonsActiveInternal: this.isUhonsActiveInternalControl?.value,
            isUhonsActiveExternal: this.isUhonsActiveExternalControl?.value,
            uhonsFundAdministrativePledgeTypeId,
            isStlWaterActiveInternal: this.isStlWaterActiveInternalControl?.value,
            isStlWaterActiveExternal: this.isStlWaterActiveExternalControl?.value,
            stlWaterFundAdministrativePledgeTypeId,
        });
        this._applicationCountyService.updateApplicationCounty(applicationCounty)
            .subscribe(() => this._router.navigate(['app/counties']));
    }

    private handleIsActiveExternalOnChange(): void {
        this.isStlWaterActiveExternalControl
            .valueChanges
            .pipe(takeUntil(this.$destroyed))
            .subscribe((active: boolean) => {
                if (!active) {
                    delete this.selectedStlWaterFundAdministrativePledgeTypeControl?.errors?.requiredWhen;
                    this.selectedStlWaterFundAdministrativePledgeTypeControl?.setValue('');
                }
            });
        this.isUhonsActiveExternalControl
            .valueChanges
            .pipe(takeUntil(this.$destroyed))
            .subscribe((active: boolean) => {
                if (!active) {
                    delete this.selectedUhonsFundAdministrativePledgeTypeControl?.errors?.requiredWhen;
                    this.selectedUhonsFundAdministrativePledgeTypeControl?.setValue('');
                }
            });
    }
}
